import React from 'react';

type Variant = 'dark' | 'light' | 'accent' | 'outline';

interface FlipButtonProps {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: Variant;
  className?: string;
  icon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const VARIANTS: Record<Variant, string> = {
  dark: 'bg-ink text-paper hover:bg-card',
  light: 'bg-paper text-ink hover:bg-surface',
  accent: 'bg-accent text-white hover:bg-accent2',
  outline: 'bg-transparent text-current border border-current/25 hover:border-current/60',
};

const SIZES = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
};

/**
 * Pill button with the stacked duplicated-label hover:
 * current label slides up and out, its twin slides in from below.
 */
const FlipButton: React.FC<FlipButtonProps> = ({
  label,
  href,
  onClick,
  variant = 'dark',
  className,
  icon,
  size = 'md',
}) => {
  const classes = `flip-btn group inline-flex items-center gap-2 rounded-full font-medium tracking-tight transition-colors duration-300 ${VARIANTS[variant]} ${SIZES[size]} ${className ?? ''}`;

  const inner = (
    <>
      <span className='flip-label'>
        <span>{label}</span>
        <span aria-hidden='true'>{label}</span>
      </span>
      {icon}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        className={classes}
      >
        {inner}
      </a>
    );
  }

  return (
    <button onClick={onClick} className={classes}>
      {inner}
    </button>
  );
};

export default FlipButton;
